import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Dumbbell, FileText, HelpCircle, LogOut, Moon, Sun, Bell, BellOff, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import {
  getMyTrainerRecord,
  getTrainerGym,
  getTrainerPtAssignments,
  getTrainerCompletedPtAssignments,
  getTrainerMembers,
  getTrainerPtLogs,
} from '../../lib/trainerDb';
import { getPtSessionsForTrainer } from '../../lib/ptSessions';
import { getActiveGymNotices } from '../../lib/gymNotices';
import { isPushSupported, getCurrentSubscription, requestAndSubscribe, unsubscribeFromPush } from '../../lib/pushNotifications';
import TrainerPT from './TrainerPT';
import TrainerHelp from './TrainerHelp';
import TrainerInfo from './TrainerInfo';

const navItems = [
  { path: '/trainer/pt', icon: FileText, label: 'PT 현황' },
  { path: '/trainer/help', icon: HelpCircle, label: '도움말' },
  { path: '/trainer/info', icon: User, label: '내 정보' },
];

export default function TrainerPortal() {
  const { signOut } = useAuth();
  const { darkMode, setDarkMode } = useApp();

  const [trainer, setTrainer] = useState(null);
  const [gym, setGym] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [completedAssignments, setCompletedAssignments] = useState([]);
  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (isPushSupported()) {
      getCurrentSubscription().then(sub => setPushSubscribed(!!sub));
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const rec = await getMyTrainerRecord();
      if (!rec) { setLoading(false); return; }
      setTrainer(rec);

      const [gymData, asgns, completedAsgns, mems, ptLogs, noticeData, sessionData] = await Promise.all([
        getTrainerGym(rec.gymId),
        getTrainerPtAssignments(rec.id),
        getTrainerCompletedPtAssignments(rec.id),
        getTrainerMembers(rec.gymId),
        getTrainerPtLogs(rec.id),
        getActiveGymNotices(rec.gymId),
        getPtSessionsForTrainer(),
      ]);
      setGym(gymData);
      setAssignments(asgns);
      setCompletedAssignments(completedAsgns);
      setMembers(mems);
      setLogs(ptLogs);
      setNotices(noticeData);
      setSessions(sessionData);
    } catch (e) {
      console.error('트레이너 데이터 로드 실패:', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">트레이너 정보를 불러올 수 없습니다</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            일시적인 오류가 발생했습니다.<br />
            헬스장 관리자에게 문의해주세요.
          </p>
          <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto">
            <LogOut className="w-4 h-4" /> 로그아웃
          </button>
        </div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Dumbbell className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">트레이너 정보 없음</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            계정이 트레이너 레코드에 연동되지 않았습니다.<br />
            헬스장 관리자에게 문의해주세요.
          </p>
          <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto">
            <LogOut className="w-4 h-4" /> 로그아웃
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* 공지 배너 */}
      {notices.map(n => (
        <div key={n.id} className="bg-blue-600 text-white px-4 py-2 text-sm flex-shrink-0">
          <span className="font-medium">[공지] </span>{n.title}
          {n.content && <span className="text-blue-100 ml-1">— {n.content}</span>}
        </div>
      ))}
      {/* 헤더 */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-white">{trainer.name?.[0] || 'T'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-gray-900 dark:text-white truncate">
            {trainer.name} 트레이너
          </div>
          {gym?.name && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{gym.name}</div>
          )}
        </div>
        <button
          onClick={() => setDarkMode(!darkMode)}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        {isPushSupported() && (
          <button
            disabled={pushLoading}
            onClick={async () => {
              setPushLoading(true);
              if (pushSubscribed) {
                await unsubscribeFromPush(null);
                setPushSubscribed(false);
              } else {
                const { error } = await requestAndSubscribe(null, 'trainer');
                if (!error) setPushSubscribed(true);
              }
              setPushLoading(false);
            }}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            title={pushSubscribed ? '알림 끄기' : '알림 켜기'}
          >
            {pushSubscribed ? <Bell className="w-4 h-4 text-blue-500" /> : <BellOff className="w-4 h-4" />}
          </button>
        )}
        <button
          onClick={signOut}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </header>

      {/* 페이지 */}
      <main className="flex-1 overflow-y-auto pb-20">
        <Routes>
          <Route
            path="/trainer/pt"
            element={
              <TrainerPT
                assignments={assignments}
                completedAssignments={completedAssignments}
                members={members}
                logs={logs}
                sessions={sessions}
                onRefresh={loadData}
              />
            }
          />
          <Route path="/trainer/help" element={<TrainerHelp />} />
          <Route path="/trainer/info" element={<TrainerInfo trainer={trainer} gym={gym} />} />
          <Route path="*" element={<Navigate to="/trainer/pt" replace />} />
        </Routes>
      </main>

      {/* 하단 탭 */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex">
        {navItems.map(({ path, icon: Icon, label }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors ${
                isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
